import Link from "next/link";

import type { TenantIdentity } from "@/config/tenants";
import { LegalPageHeader } from "@/features/legal/components/legal-page-header";
import {
  cookiePolicyCopy,
  type CookiePolicySection,
} from "@/features/marketing/content/cookie-policy-copy";
import type { MarketingLocale } from "@/features/marketing/content/marketing-copy";
import {
  MarketingFooter,
  MarketingHeader,
} from "@/features/marketing/components/marketing-home";

import styles from "./cookie-policy-page.module.css";
import marketingStyles from "./marketing-home.module.css";

type CookiePolicyPageProps = {
  locale: MarketingLocale;
  tenant?: TenantIdentity;
};

export function CookiePolicyPage({ locale, tenant }: CookiePolicyPageProps) {
  const copy = cookiePolicyCopy[locale];
  const policyPath = tenant ? `/${tenant.code}/cookies` : "/cookies";

  const policy = (
    <main className={styles.page}>
      <article className={styles.policy}>
        {tenant ? <LegalPageHeader tenant={tenant} /> : null}

        <div className={styles.titleBlock}>
          <p>{copy.version}</p>
          <h1>{copy.title}</h1>
          <p className={styles.precedence}>{copy.precedence}</p>
        </div>

        {copy.sections.slice(0, 3).map((section) => (
          <PolicySection key={section.title} section={section} />
        ))}

        <section className={styles.section} aria-labelledby="cookie-summary-heading">
          <h2 id="cookie-summary-heading">{copy.table.title}</h2>
          <div className={styles.tableWrap}>
            <table>
              <thead>
                <tr>{copy.table.headers.map((header) => <th key={header} scope="col">{header}</th>)}</tr>
              </thead>
              <tbody>
                {copy.table.records.map((record) => (
                  <tr key={record.name}>
                    <th scope="row"><code>{record.name}</code></th>
                    <td>{record.category}</td>
                    <td>{record.provider}</td>
                    <td>{record.purpose}</td>
                    <td>{record.retention}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>

        <PolicySection section={copy.sections[3]} />
        <ul className={styles.browserList}>
          {copy.browserInstructions.map((instruction) => <li key={instruction}>{instruction}</li>)}
        </ul>
        <p className={styles.note}>{copy.browserNote}</p>

        <section className={styles.section}>
          <h2>{copy.contact.title}</h2>
          <p>{copy.contact.intro}</p>
          <address>
            {copy.contact.details.map((detail) => <span key={detail}>{detail}</span>)}
          </address>
        </section>

        <Link
          href={tenant ? `/${tenant.code}/login` : `/?lang=${locale}`}
          className={styles.backLink}
        >
          ← {copy.backHome}
        </Link>
      </article>
    </main>
  );

  if (tenant) return policy;

  return (
    <div className={marketingStyles.page}>
      <MarketingHeader locale={locale} localePath={policyPath} linkToHomeSections />
      {policy}
      <MarketingFooter locale={locale} linkToHomeSections />
    </div>
  );
}

function PolicySection({ section }: { section: CookiePolicySection }) {
  return (
    <section className={styles.section}>
      <h2>{section.title}</h2>
      {section.paragraphs?.map((paragraph) => <p key={paragraph}>{paragraph}</p>)}
      {section.bullets ? (
        <ul>{section.bullets.map((bullet) => <li key={bullet}>{bullet}</li>)}</ul>
      ) : null}
      {section.subsections?.map((subsection) => (
        <section className={styles.subsection} key={subsection.title}>
          <h3>{subsection.title}</h3>
          {subsection.paragraphs.map((paragraph) => <p key={paragraph}>{paragraph}</p>)}
        </section>
      ))}
    </section>
  );
}
